<script>
import { FormModel } from '@/components/global/form';
import { meta } from 'utils/DOM';
import { scrollToError } from '../../utilities/helpers';

// Kept in sync with the `documents` max rule in FinanceController::store().
const MAX_DOCUMENTS = 5;

// Field names as shown on the finance step, so every summary message says where the problem is.
const FIELD_LABELS = {
    capital: 'Įstatinis kapitalas, Eur',
    turnover_previous_year: 'Praėjusių metų apyvarta, Eur',
    turnover_next_year: 'Planuojama ateinančiais metais apyvarta, Eur',
    assets_previous_year: 'Praėjusių metų turtas (aktyvai), Eur',
    employees_count_previous_year: 'Darbuotojų skaičius (praėjusių metų)',
    electronic_access_code: 'Elektroninis sertifikuotas Juridinių asmenų išrašo (ESI) prieigos raktas',
    legal_entity_category_id: 'Juridinio asmens kategorija',
    register_type: 'Narystės tipas',
    no_other_fi_accounts: 'Kliento sąskaitos kitose finansų įstaigose',
    accounts: 'Kliento sąskaitos kitose finansų įstaigose',
    transfers: 'Šalys, iš kurių bus gaunamos ir / arba pervedamos lėšos',
    documents: 'Dokumentai',
};

const LIST_FIELD_LABELS = {
    accounts: { item: 'sąskaita', fields: { institution: 'Finansų įstaigos pavadinimas', country_id: 'Valstybė', country: 'Valstybė' } },
    transfers: { item: 'šalis', fields: { country_id: 'Šalis', country: 'Šalis' } },
    documents: { item: 'dokumentas', fields: { file: 'Dokumento failas', type: 'Dokumento tipas' } },
};

function fieldLabel(key) {
    const [list, index, field] = key.split('.');

    if (index === undefined) {
        return FIELD_LABELS[key] || null;
    }

    const listLabels = LIST_FIELD_LABELS[list];
    const label = listLabels && listLabels.fields[field];

    return label ? `${label} (${listLabels.item} nr. ${Number(index) + 1})` : null;
}

export default {
    name: 'CcFinanceForm',

    props: {
        initialData: null,
    },

    data() {
        return {
            loading: false,
            model: null,
            activeUploadType: 'incorporation',
        };
    },

    created() {
        this.model = new FormModel(this.initialData).transform((data) => {
            data.documents = data.documents.map((document) => ({
                file: document.file,
                type: document.type,
            }));

            return data;
        });

        if (this.model.$.documents) {
            this.model.$.documents.forEach((doc) => {
                if (!doc.type) {
                    doc.type = 'incorporation';
                }
            });
        }

        this.$watch('model.$.register_type', () => {
            this.model.$.documents = [];
        });
    },

    computed: {
        canSubmit() {
            if (!this.model) {
                return false;
            }

            // A document without `file` never finished uploading - submitting it
            // fails server side validation on a field that has nowhere to show.
            if (this.model.$.documents.some((document) => document.uploading || document.error || !document.file)) {
                return false;
            }

            return true;
        },

        canAddDocuments() {
            return this.model ? this.model.$.documents.length < MAX_DOCUMENTS : false;
        },

        errorMessages() {
            const bag = this.model && this.model.$e.errors ? this.model.$e.errors.errors : null;

            if (!bag) {
                return [];
            }

            const messages = [];

            Object.keys(bag).forEach((key) => {
                const label = fieldLabel(key);

                [].concat(bag[key]).forEach((message) => {
                    const text = label ? `${label}: ${message}` : message;

                    if (!messages.includes(text)) {
                        messages.push(text);
                    }
                });
            });

            return messages;
        },

        hasErrors() {
            return this.errorMessages.length > 0;
        },
    },

    methods: {
        getDocuments(type) {
            return this.model ?
                this.model.$.documents.filter(d => d.type === type) :
                [];
        },

        addAccount() {
            this.model.$.accounts.push({
                institution: '',
                country: '',
                country_id: null,
            });
        },

        removeAccount(account) {
            this.model.$.accounts = this.model.$.accounts.filter(s => s !== account);
        },

        addTransfer() {
            this.model.$.transfers.push({
                country: '',
                country_id: null,
            });
        },

        removeTransfer(transfer) {
            this.model.$.transfers = this.model.$.transfers.filter(s => s !== transfer);
        },

        documentsChanged(e) {
            if (!e.target.files.length) {
                return;
            }

            Array.from(e.target.files).forEach((file) => {
                this.model.$.documents.push({
                    _file: file,
                    file: null,
                    uploading: false,
                    cancelToken: null,
                    error: null,
                    type: this.activeUploadType,
                });
            });

            e.target.value = '';

            this.uploadFiles();
        },

        addDocuments(type = 'incorporation') {
            this.activeUploadType = type;
            const fileInput = this.$refs.documentSelect;
            fileInput.click();
        },

        removeDocument(document) {
            const index = this.model.$.documents.indexOf(document);
            if (index === -1) {
                return;
            }

            if (document.cancelToken) {
                document.cancelToken.abort();
                document.cancelToken = null;
            }

            this.model.$.documents.splice(index, 1);
        },

        async uploadFiles() {
            for (const entry of this.model.$.documents) {
                if (!entry._file || entry.file || entry.uploading) {
                    continue;
                }

                entry.uploading = true;
                entry.cancelToken = new AbortController();

                const formData = new FormData();
                formData.append('_token', meta('csrf-token'));
                formData.append('file', entry._file);

                fetch('/legal-entity/finance/documents', {
                    method: 'POST',
                    body: formData,
                    signal: entry.cancelToken.signal,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            return response
                                .json()
                                .then((data) => {
                                    entry.uploading = false;
                                    entry.cancelToken = null;
                                    entry.file = data.data.file;

                                    return data;
                                });
                        }

                        if (response.status === 422) {
                            return response.json()
                                .then((data) => {
                                    entry.error = data.error.errors.file.join(', ');
                                    entry.uploading = false;
                                    entry.cancelToken = null;

                                    return data;
                                });
                        }

                        // Not sure about use cases here, so just report error
                        entry.error = 'Įvyko klaida įkeliant dokumentą';
                        entry.uploading = false;
                        entry.cancelToken = null;

                        return null;
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') {
                            this.removeDocument(entry);
                            return;
                        }

                        entry.uploading = false;
                        entry.cancelToken = null;
                        entry.error = 'Įvyko klaida įkeliant dokumentą';

                        this.$sentyReporting.report(error);
                    });
            }
        },

        async submit() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.model.$e.clear();

            try {
                const response = await this.$http.post('', this.model.data);
                window.location = response.data.data.redirect;
            } catch (e) {
                this.model.$e.updateFromResponse(e);
                scrollToError();
                this.$sentyReporting.report(e);
            }

            this.loading = false;
        },
    },
};
</script>
